import {
  ActionPattern,
  all,
  call,
  fork,
  put,
  SagaReturnType,
  select,
  takeLatest,
} from 'redux-saga/effects'
import { Saga } from 'redux-saga'
import { changeStartDateOfPlan } from 'src/graphql/Services/User/mutations'
import { ServiceRequestAction } from 'src/helpers/ReduxHelpers'

import { actions, selectors } from '../index'
import { MenuDay, Recipe } from '../../types/fam-graphql'
import {
  CacheNames,
  CacheType,
  deleteCache,
  getCacheEntries,
  offlineQuery,
  saveCache,
} from '../../utils/CacheUtils'
import Router from '../../routes/router'
import definitions from '../../routes/definitions'
import ApiSagas from '../api/sagas'
import * as Recipes from '../../graphql/Services/Recipes'
import { api, app } from '../../configuration'
import PlanSagas from '../plan/sagas'
import {
  currentPlan,
  notificationPreferences,
} from '../../graphql/Services/User/queries'
import { labels } from '../../graphql/Services/Translations/queries'

enum LoadableType {
  page = 'page',
  recipe = 'recipe',
  image = 'image',
  query = 'query',
}

type Loadable = {
  slug: string
  type: LoadableType
  progress: number
}

export default class PlanningSagas {
  static *init() {
    const isOffline = yield select(selectors.app.isOffline)
    const currentPlan = yield select(selectors.user.currentPlan)
    if (!currentPlan && !isOffline) {
      yield put(actions.app.setIsLoading(true))
    }
  }

  static *load() {
    yield PlanSagas.loadCurrentPlan()
    const currentPlan = yield select(selectors.user.currentPlan)
    if (currentPlan) {
      yield put(actions.app.setIsLoading(false))
    }
  }

  static *getToLoadList() {
    const currentPlan = yield select(selectors.user.currentPlan)
    const menu: MenuDay[] = currentPlan?.menu
    const recipes: Loadable[] =
      menu
        ?.reduce(
          (arr, day) =>
            arr.concat(
              day?.moments?.reduce(
                (agg, moment) => agg.concat(moment?.meal ?? []),
                [] as Recipe[]
              ) ?? []
            ),
          [] as Recipe[]
        )
        ?.map((r: Recipe) => ({
          slug: r.slug,
          type: LoadableType.recipe,
          progress: 0,
        })) ?? []

    const pages: Loadable[] = [
      Router.getRouteUrl(definitions.onboarding.name) + '/',
      Router.getRouteUrl(definitions.planning.name),
      Router.getRouteUrl(definitions.shoppinglist.name),
      Router.getRouteUrl(definitions.hebdo.name),
      Router.getRouteUrl(definitions.settings.name),
      Router.getRouteUrl(definitions.offline.name),
    ]?.map((slug) => ({
      slug,
      type: LoadableType.page,
      progress: 0,
    }))

    const queries: Loadable[] = [
      {
        slug: 'translations',
        type: LoadableType.query,
        progress: 0,
      },
      {
        slug: 'currentPlan',
        type: LoadableType.query,
        progress: 0,
      },
      {
        slug: 'notificationPreferences',
        type: LoadableType.query,
        progress: 0,
      },
    ]

    return pages?.concat(recipes)?.concat(queries)
  }

  static *check() {
    yield put(actions.planning.setIsCheckingCache(true))
    const toLoad: Loadable[] = yield call(PlanningSagas.getToLoadList)
    const slugsToLoad = toLoad
      ?.filter((l) => l.type !== LoadableType.query)
      ?.map(
        ({ type, slug }) =>
          app.APP_URL +
          (type === LoadableType.recipe
            ? Router.getRouteUrl(definitions.recipe.name, { slug })
            : slug)
      )
    const loaded = yield call(getCacheEntries, CacheNames[CacheType.page])
    const loadedSlugs = loaded?.map((l: Response) => l.url)
    const found = slugsToLoad?.find(
      (slug: string) => !loadedSlugs.includes(slug)
    )

    if (loadedSlugs?.length > 0 && found) {
      console.log('[OFFLINE] found missing entry', found)
    }

    if (!found && slugsToLoad?.length > 0) {
      yield put(actions.planning.setProgress(1))
    } else {
      yield put(actions.planning.setProgress(0))
    }
    yield put(actions.planning.setIsCheckingCache(false))
  }

  static *download() {
    const toLoad: Loadable[] = yield call(PlanningSagas.getToLoadList)
    console.log('toLoad', toLoad)

    yield all(
      toLoad.map((loadable, index) => {
        try {
          return call(
            loadable.type === LoadableType.image
              ? PlanningSagas.downloadImage
              : loadable.type === LoadableType.recipe
              ? PlanningSagas.downloadRecipe
              : loadable.type === LoadableType.page
              ? PlanningSagas.downloadPage
              : PlanningSagas.downloadQuery,
            loadable?.slug,
            function* (progress) {
              toLoad[index].progress = progress
              yield put(
                actions.planning.setProgress(
                  toLoad.reduce((arr, e) => arr + e.progress, 0) / toLoad.length
                )
              )
            }
          )
        } catch (e) {
          console.error('Download error', e)
          return null
        }
      })
    )

    yield put(actions.planning.setProgress(1))
  }

  static *remove() {
    yield call(deleteCache, CacheNames[CacheType.page])
    yield call(deleteCache, CacheNames[CacheType.image])
    yield call(deleteCache, CacheNames[CacheType.data])
    yield put(actions.planning.setProgress(0))
  }

  static *downloadImage(slug: string, callback: (progress: number) => void) {
    try {
      const url = slug

      const response: SagaReturnType<any> = yield call(
        fetch,
        url?.replace((app.USE_IMAGE_PROXY ? api.API_URL : '') ?? '', '')
      )
      yield call(saveCache, CacheNames[CacheType.image], slug, response)
      yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading image', e)
    }
  }

  static *downloadRecipe(slug: string, callback: (progress: number) => void) {
    try {
      const url = Router.getRouteUrl(definitions.recipe.name, { slug })
      yield fork(PlanningSagas.downloadPage, url)

      const rs = yield call(
        offlineQuery,
        url,
        () => call(ApiSagas.query, Recipes.queries.recipeBySlug({ slug })),
        true
      )
      const recipe: Recipe = rs?.data
      if (recipe?.image) {
        yield call(callback, 0.5)
        const response = yield call(
          fetch,
          recipe?.image?.replace(
            (app.USE_IMAGE_PROXY ? api.API_URL : '') ?? '',
            ''
          )
        )
        yield call(
          saveCache,
          CacheNames[CacheType.image],
          recipe?.image,
          response
        )
      }
      yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading recipe', e)
    }
  }

  static *downloadPage(url: string, callback?: (progress: number) => void) {
    try {
      const response = yield call(fetch, url)
      yield call(saveCache, CacheNames[CacheType.page], url, response)
      if (callback) yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading page', e)
    }
  }

  // TODO: refactor this to have a single downloadQuery function
  static *downloadQuery(query: string, callback?: (progress: number) => void) {
    switch (query) {
      case 'currentPlan':
        yield call(PlanningSagas.downloadCurrentPlan, query, callback)
        break
      case 'translations':
        yield call(PlanningSagas.downloadTranslations, query, callback)
        break
      case 'notificationPreferences':
        yield call(
          PlanningSagas.downloadNotificationPreferences,
          query,
          callback
        )
        break
    }
  }

  static *downloadCurrentPlan(
    _: string,
    callback?: (progress: number) => void
  ) {
    try {
      yield call(
        offlineQuery,
        'currentPlan',
        () => call(ApiSagas.query, currentPlan()),
        true
      )
      if (callback) yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading currentPlan', e)
    }
  }

  static *downloadTranslations(
    _: string,
    callback?: (progress: number) => void
  ) {
    try {
      yield call(
        offlineQuery,
        'translations',
        () =>
          call(
            ApiSagas.query,
            labels({
              localeFilters: { locale: 'fr' },
            })
          ),
        true
      )
      if (callback) yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading translations', e)
    }
  }

  static *downloadNotificationPreferences(
    _: string,
    callback?: (progress: number) => void
  ) {
    try {
      yield call(
        offlineQuery,
        'notificationPreferences',
        () => call(ApiSagas.query, notificationPreferences()),
        true
      )
      if (callback) yield call(callback, 1)
    } catch (e) {
      console.error('Error downloading notificationPreferences', e)
    }
  }

  static *changeStartDateOfPlan(action: ServiceRequestAction) {
    const { payload } = action
    const { data: results } = yield call(
      ApiSagas.mutate,
      changeStartDateOfPlan(payload?.params)
    )

    if (!results.error) {
      yield put(actions.user.setCurrentPlan(results))
    }
  }

  static *loop() {
    yield all([
      takeLatest(
        actions.planning.download as ActionPattern,
        this.download as Saga
      ),
      takeLatest(actions.planning.remove as ActionPattern, this.remove as Saga),
      takeLatest(actions.planning.check as ActionPattern, this.check as Saga),
      takeLatest(
        actions.planning.changeStartDateOfPlanRequest as ActionPattern,
        this.changeStartDateOfPlan
      ),
      takeLatest(actions.planning.load as ActionPattern, this.load),
    ])
  }
}
